import { ImageCompressionService, ImageSelectionService } from 'src/easyclaim.app.services';
import { Image } from 'src/easyclaim.app.services/ImageSelectionService';
import { usePhotoThumbnailStore } from 'src/easyclaim.app.stores/usePhotoThumbnailStore';

const THUMBNAIL_MAX_WIDTH = 400;
const THUMBNAIL_MAX_HEIGHT = 300;
const THUMBNAIL_QUALITY = 0.8;

const thumbnailCompressionService = new ImageCompressionService({
	maxWidth: THUMBNAIL_MAX_WIDTH,
	maxHeight: THUMBNAIL_MAX_HEIGHT,
	quality: THUMBNAIL_QUALITY,
});

const pendingThumbnails = new Map<string, Promise<void>>();

function dataUrlToBlob(dataUrl: string): Blob {
	const [metadata, content] = dataUrl.split(',');

	if (!metadata || !content) {
		throw new Error('Cannot create a thumbnail from an invalid data URL');
	}

	const mimeType = metadata.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
	const bytes = atob(content);
	const buffer = new Uint8Array(bytes.length);

	for (let index = 0; index < bytes.length; index++) {
		buffer[index] = bytes.charCodeAt(index);
	}

	return new Blob([buffer], { type: mimeType });
}

async function createAndCacheThumbnail(imageId: string, image: Image): Promise<void> {
	const dataUrl = ImageSelectionService.getImageAsDataUrl(image);

	if (!dataUrl) {
		throw new Error('Cannot create a thumbnail from an image without content');
	}

	const sourceBlob = dataUrlToBlob(dataUrl);
	const sourceFile = new File([sourceBlob], image.document?.fileName ?? `${imageId}.jpg`, {
		type: sourceBlob.type,
	});
	const thumbnailDataUrl = await thumbnailCompressionService.compressToDataUrl(sourceFile);

	usePhotoThumbnailStore.getState().setThumbnail(imageId, dataUrlToBlob(thumbnailDataUrl));
}

export function cacheLegacyImageThumbnail(imageId: string, image: Image): Promise<void> {
	const state = usePhotoThumbnailStore.getState();
	const existingEntry = state.entriesByImageId[imageId];

	if (existingEntry && existingEntry.expiresAt > Date.now()) {
		return Promise.resolve();
	}

	const pendingThumbnail = pendingThumbnails.get(imageId);

	if (pendingThumbnail) {
		return pendingThumbnail;
	}

	const thumbnail = createAndCacheThumbnail(imageId, image).finally(() => {
		pendingThumbnails.delete(imageId);
	});

	pendingThumbnails.set(imageId, thumbnail);

	return thumbnail;
}
